import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { debounceTime, switchMap, tap, catchError } from 'rxjs/operators';
import { PharmacyService } from 'app/shared/services/pharmacies/pharmacy.service';
import { Pharmacy } from 'app/shared/models/pharmacy.model';
import { Router } from '@angular/router';

export interface PharmacySearchDialogData {
  title?: string;
  description?: string;
  defaultLocationId?: number;
  checkPharmacyEmail?: boolean;
  showEmail?: boolean;
  preferredPharmacy?: string;
  confirmLabel?: string;
  dismissible?: boolean;
}

@Component({
  selector: 'app-pharmacy-search-dialog',
  templateUrl: './pharmacy-search-dialog.component.html',
  styleUrls: ['./pharmacy-search-dialog.component.scss']
})
export class PharmacySearchDialogComponent implements OnInit {
  locationSearchTerm = new FormControl('');
  filteredPharmacies$: Observable<Pharmacy[]> = of([]);
  searching = false;
  selectedPharmacy: Pharmacy = null;
  showCreateNewStoreOption: boolean = false;
  preferredPharmacy: string | null = null;
  alertMessage: string = `Looks like this pharmacy doesn't have an email yet.`;

  constructor(
    public dialogRef: MatDialogRef<PharmacySearchDialogComponent, Pharmacy>,
    @Inject(MAT_DIALOG_DATA) public data: PharmacySearchDialogData,
    private pharmacyService: PharmacyService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.setupPharmacySearchObservable();
    if (this.data.defaultLocationId) {
      this.preSelectPharmacy(this.data.defaultLocationId);
    }

    if (this.data.preferredPharmacy) {
      this.preferredPharmacy = this.data.preferredPharmacy;
    }
  }

  setupPharmacySearchObservable(): void {
    this.filteredPharmacies$ = this.locationSearchTerm.valueChanges.pipe(
      debounceTime(250),
      tap(() => {
        this.showCreateNewStoreOption = false;
        this.searching = true;
      }),
      switchMap((term: string) => {
        if (typeof term !== 'string' || term === '') {
          this.searching = false;
          return of([]);
        }
        const trimmed = term.trim();
        return this.pharmacyService.locationSearch({ searchTerm: trimmed }).pipe(
          catchError(() => of([])),
          tap(pharmacies => {
            this.searching = false;
            this.showCreateNewStoreOption = pharmacies.length === 0;
          })
        );
      })
    );
  }

  preSelectPharmacy(locationId?: number): void {
    this.searching = true;
    this.showCreateNewStoreOption = false;

    this.pharmacyService.locationSearch({ locationid: locationId }).pipe(
      catchError(() => of([])),
      tap(pharmacies => {
        this.searching = false;
        if (!pharmacies || pharmacies.length === 0) {
          this.showCreateNewStoreOption = true;
          return;
        }
        this.selectPharmacy(pharmacies[0]);
      })
    ).subscribe();
  }

  displayFn = (pharmacy: Pharmacy | null): string => {
    if (!pharmacy || typeof pharmacy !== 'object') return '';
    return `${pharmacy.locationid} - ${pharmacy.locationname}`;
  };

  selectPharmacy(pharmacy: Pharmacy): void {
    this.selectedPharmacy = pharmacy;
    this.locationSearchTerm.disable();
    this.locationSearchTerm.setValue(pharmacy);
  }

  confirmSelection(): void {
    if (!this.selectedPharmacy) return;
    this.dialogRef.close(this.selectedPharmacy);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  clearSelection(): void {
    this.selectedPharmacy = null;
    this.locationSearchTerm.setValue('');
    this.locationSearchTerm.enable();
  }

  redirectToCreateStore(): void {
    this.closeDialog();
    this.router.navigate(['stores', 'create-store'], {
      queryParams: {
        returnTo: this.router.url,
        ...(this.preferredPharmacy
          ? { pharmacy: encodeURIComponent(JSON.stringify(this.preferredPharmacy)) }
          : {})
      }
    });
  }

  goToStore(): void {
    this.dialogRef.close();
    this.router.navigate(['/stores', 'store-detail'], {
      queryParams: {
        location: this.selectedPharmacy.locationid,
        returnTo: this.router.url,
        missingEmail: true
      }
    });
  }

  get isEmailMissing(): boolean {
    return this.data.checkPharmacyEmail && this.selectedPharmacy && !this.selectedPharmacy?.email?.trim();
  }
}